import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { colorStrings, fontFamily, mediaQueriesBP } from "@waytek/Utilities/variables";
import * as cssLinter from "css";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    label = "label",
    link = "link",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.customCSS]: string;
    };
}

const mapDispatchToProps = {
    subscribe,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailButton?: ButtonPresentationProps;
}

export const subscribeStyles: SubscribeStyles = {
    mainGridContainer: {
        gap: 0,
        css: css`
            width: 100%;
            display: flex;
            margin-top: 21px;
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleLabel: {
        fontFamily,
        size: "16px",
        lineHeight: "32px",
        color: colorStrings.commonGlobalSecondaryGray,
        weight: 700,
        css: css`
            ${mediaQueriesBP.maxMD} {
                line-height: 24px;
            }
        `,
    },
    emailGridItem: {
        width: 12,
        css: css`
            &&& {
                ${mediaQueriesBP.maxMD} {
                    margin-top: 12px;
                }
                ${mediaQueriesBP.minMD} {
                    margin-top: 9px;
                }
            }
        `,
    },
    emailButton: {
        css: css`
            width: 100%;
        `,
    },
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.email-grid-item{
}

.email-button{
}
`;

export const CmsSubscribe = ({ fields, subscribe, id }: Props) => {
    const { url } = useGetLink(fields.link);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const styles = useMergeStyles("subscribe", subscribeStyles);

    const onSubscribeClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
        <GridContainer className="main-grid-container" {...styles.mainGridContainer}>
            {fields.title && (
                <GridItem className="title-grid-item" {...styles.titleGridItem}>
                    <Typography className="title" {...styles.titleLabel}>{fields.title}</Typography>
                </GridItem>
            )}
            {fields.link && (
                <GridItem className="email-grid-item" {...styles.emailGridItem}>
                    <Button className="email-button" {...styles.emailButton} onClick={onSubscribeClick}>
                        {fields.label}
                    </Button>
                </GridItem>
            )}
        </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe) as any,
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                allowUrls: item => item.fields.linkType === "Link",
                showCategoryRoot: item => true,
                fieldType: "General",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
